import { parsePipeResult } from "#web/services/tinybird";
import { fetchTinybird } from "#web/services/tinybird";
import { Banner, Box, Card, DataTable, Divider, Icon, InlineStack, Text, Tooltip } from "@shopify/polaris";
import { InfoIcon } from "@shopify/polaris-icons";
import { queryOptions, useQuery } from "@tanstack/react-query";
import * as v from "valibot";

import { SpinnerCentered } from "../common/SpinnerCentered/SpinnerCentered";
import { ShopQuery } from "../shop/queries/shopQuery";
import { makeShop } from "../shop/queries/shopQuery";
import { extensionMap } from "./extension-map";

const ExtensionAttributionListQuery = ({
  endDate,
  myshopifyDomain,
  startDate,
}: {
  endDate: string;
  myshopifyDomain: string | undefined;
  startDate: string;
}) =>
  queryOptions({
    enabled: !!myshopifyDomain,
    queryFn: () => {
      const ExtensionAttributionListData = v.array(
        v.object({
          extension: v.string(),
          percentage: v.number(),
          total: v.number(),
        }),
      );
      return fetchTinybird("extension_attribution_list", { endDate, shopId: myshopifyDomain!, startDate }).then(
        parsePipeResult(ExtensionAttributionListData),
      );
    },
    queryKey: ["extension_attribution_list", myshopifyDomain, startDate, endDate],
  });

export default function ExtensionAttributionList({ dateRange }: { dateRange: { end: string; start: string } }) {
  const { data: shopData, isError: shopError, isFetching: shopFetching } = useQuery(ShopQuery());
  const shop = shopData ? makeShop(shopData) : null;
  const {
    data: extensionAttributionListData,
    isError: extensionAttributionListError,
    isFetching: extensionAttributionListFetching,
  } = useQuery(
    ExtensionAttributionListQuery({
      endDate: dateRange.end,
      myshopifyDomain: shop!.myshopifyDomain!,
      startDate: dateRange.start,
    }),
  );

  const isError = shopError || extensionAttributionListError;
  const isLoading = shopFetching || extensionAttributionListFetching;

  if (isError) {
    return (
      <Banner title="Error" tone="critical">
        There was an error loading the extension attribution list
      </Banner>
    );
  }

  return (
    <Card background="bg-surface-secondary" padding="0">
      <Box padding="400">
        <InlineStack gap="100">
          <Text as="h2" variant="headingMd">
            Extension Breakdown
          </Text>
          <Tooltip content="Extensions detected on checkout" width="wide">
            <Icon source={InfoIcon} tone="base" />
          </Tooltip>
        </InlineStack>
      </Box>
      <Divider />

      {isLoading ? (
        <SpinnerCentered />
      ) : extensionAttributionListData!.data.length > 0 ? (
        <DataTable
          columnContentTypes={["text", "numeric", "numeric"]}
          footerContent={`Detected ${String(extensionAttributionListData!.data.length)} extensions`}
          hasZebraStripingOnData={true}
          headings={[
            <Text as="span" fontWeight="medium" variant="bodyMd">
              Extension
            </Text>,
            <Text as="span" fontWeight="medium" variant="bodyMd">
              Percentage of visitors with extension on checkout
            </Text>,
            <Text as="span" fontWeight="medium" variant="bodyMd">
              Total visitors with extension on checkout
            </Text>,
          ]}
          increasedTableDensity={true}
          rows={extensionAttributionListData!.data.map((row) => [
            <Text as="span" fontWeight="bold" variant="bodyMd">
              {extensionMap[row.extension]?.label ?? row.extension}
            </Text>,
            <Text as="span" variant="bodyMd">
              {row.percentage}%
            </Text>,
            <Text as="span" variant="bodyMd">
              {row.total}
            </Text>,
          ])}
        />
      ) : (
        <Box background="bg-surface" padding="600">
          <Text alignment="center" as="span" variant="bodyMd">
            No extensions detected
          </Text>
        </Box>
      )}
    </Card>
  );
}
